import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import InduceXTResources from "@/components/site/InduceXTResources";
import ProductComparison from "@/components/site/ProductComparison";

const GROWTH_FACTORS = [
    {
        label: "BMP-2",
        name: "Bone Morphogenetic Protein-2",
        body: "Drives new bone formation by signaling stem cells to become bone-forming osteoblasts at the graft site.",
    },
    {
        label: "BMP-7",
        name: "Bone Morphogenetic Protein-7",
        body: "Another key osteoinductive protein that supports bone regeneration and healing, especially in challenging graft environments.",
    },
    {
        label: "TGF-β1",
        name: "Transforming Growth Factor-Beta 1",
        body: "Regulates cell proliferation and matrix production during the early inflammatory and repair phases of bone healing.",
    },
    {
        label: "VEGF",
        name: "Vascular Endothelial Growth Factor",
        body: "Drives new blood-vessel formation (angiogenesis) — critical for delivering oxygen and nutrients to healing bone.",
    },
    {
        label: "PDGF",
        name: "Platelet-Derived Growth Factor",
        body: "Recruits repair cells and stimulates the early cellular response required to initiate bone and soft-tissue healing.",
    },
];

const PROCEDURES = ["Ankle fusion", "Subtalar fusion", "TTC fusion", "Triple arthrodesis"];

export default function InduceXTPage() {
    const BROCHURE_URL = "https://www.inducext.com/";
    const JOIN_URL = "/for-reps";

    const productLd = {
        "@context": "https://schema.org",
        "@type": "MedicalDevice",
        name: "Induce XT",
        alternateName: "InduceXT",
        category: "Bone allograft",
        description:
            "Induce XT is a human bone allograft with multiple native growth factors. Its proprietary Natural Matrix Protein (NMP) processing is designed to increase the bioavailability of growth factors naturally present in donor bone for foot and ankle bone grafting.",
        url: "https://1099er.co/inducext",
        manufacturer: {
            "@type": "Organization",
            name: "Induce Biologics",
            url: "https://www.inducext.com/",
        },
    };

    return (
        <div data-testid="inducext-page" className="bg-white">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(productLd) }}
            />
            {/* HERO */}
            <section data-testid="inducext-hero" className="border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
                    <h1
                        data-testid="inducext-page-headline"
                        className="sr-only"
                    >
                        Induce XT — NMP Bone Graft
                    </h1>
                    <img
                        src="/inducext-wordmark.jpg"
                        alt="Induce XT — NMP Bone Graft"
                        data-testid="inducext-page-wordmark"
                        className="w-full max-w-[560px] h-auto"
                        loading="eager"
                    />
                    <p
                        data-testid="inducext-page-subheadline"
                        className="mt-5 font-display text-2xl md:text-4xl font-semibold tracking-tight text-slate-500 leading-[1.15] max-w-3xl"
                    >
                        A bone allograft with multiple growth factors. Amplify your fusions.
                    </p>
                    <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
                        Induce XT uses proprietary Natural Matrix Protein (NMP)
                        processing to increase the bioavailability of growth factors
                        naturally present in donor bone&mdash;including BMP-2, BMP-7,
                        PDGF, VEGF, and TGF-β1&mdash;for bone regeneration.
                    </p>

                    <ul
                        data-testid="inducext-hero-benefits"
                        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3"
                    >
                        {[
                            {
                                title: "Multiple native growth factors",
                                body: "Including BMP-2, BMP-7, PDGF, VEGF, and TGF-β1.",
                            },
                            {
                                title: "NMP processing",
                                body: "Makes growth factors within the bone matrix more bioavailable.",
                            },
                            {
                                title: "Bone-based composition",
                                body: "Processed bone fibers and microparticulates combined with mineralized cancellous bone.",
                            },
                        ].map((b) => (
                            <li
                                key={b.title}
                                data-testid={`inducext-hero-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-4"
                            >
                                <p className="font-display text-sm md:text-base font-semibold text-slate-900 leading-snug">
                                    {b.title}
                                </p>
                                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                                    {b.body}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
                        <Link
                            to={JOIN_URL}
                            data-testid="inducext-hero-join-btn"
                            className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:bg-slate-800 transition-colors"
                        >
                            Schedule a Confidential Call
                            <ArrowRight size={16} />
                        </Link>
                        <a
                            href="https://drive.google.com/file/d/1nL97CndBRP1nYOxPcBs3scrW_HdQI37A/view"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inducext-hero-brochure-btn"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-300 text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:border-slate-900 transition-colors"
                        >
                            View Product Brochure
                            <ArrowRight size={16} />
                        </a>
                        <a
                            href={BROCHURE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inducext-hero-website-btn"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-300 text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:border-slate-900 transition-colors"
                        >
                            Visit Induce XT Website
                            <ArrowRight size={16} />
                        </a>
                    </div>
                </div>
            </section>

            {/* PRACTICAL HANDLING */}
            <section
                data-testid="inducext-handling-section"
                className="border-b border-slate-200 bg-white"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
                    <div className="max-w-3xl">
                        <p className="eyebrow">Handling</p>
                        <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                            Practical handling for the OR.
                        </h2>
                    </div>
                    <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {[
                            {
                                title: "Putty-like feel",
                                body: "Handles like a putty for straightforward delivery.",
                            },
                            {
                                title: "Placement that holds",
                                body: "Packable graft designed to withstand irrigation at the fusion site.",
                            },
                            {
                                title: "Flexible rehydration",
                                body: "Rehydrates with saline, blood, or bone marrow aspirate.",
                            },
                            {
                                title: "Ambient storage",
                                body: "No freezer or thawing required.",
                            },
                        ].map((b) => (
                            <li
                                key={b.title}
                                data-testid={`inducext-handling-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-4"
                            >
                                <p className="font-display text-sm md:text-base font-semibold text-slate-900 leading-snug">
                                    {b.title}
                                </p>
                                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                                    {b.body}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* NMP TECHNOLOGY */}
            <section data-testid="inducext-technology-section" className="border-b border-slate-200 bg-[#FAFAFA]">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
                    <div className="max-w-3xl">
                        <p className="eyebrow">NMP Technology</p>
                        <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                            More accessible natural growth factors.
                        </h2>
                        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                            Induce XT uses proprietary Natural Matrix Protein (NMP)
                            processing to make growth factors naturally present in
                            donor bone more available for bone regeneration.
                        </p>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-2">
                        {GROWTH_FACTORS.map((g) => (
                            <div
                                key={g.label}
                                data-testid={`inducext-growth-factor-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="group relative"
                            >
                                <button
                                    type="button"
                                    aria-label={`${g.label} — ${g.name}`}
                                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-900 font-medium cursor-help hover:border-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
                                >
                                    {g.label}
                                </button>
                                <div
                                    role="tooltip"
                                    data-testid={`inducext-growth-factor-tooltip-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-64 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-150"
                                >
                                    <div className="rounded-lg bg-slate-900 text-white p-3 shadow-lg">
                                        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                                            {g.label}
                                        </p>
                                        <p className="mt-1 text-sm font-semibold text-white leading-tight">
                                            {g.name}
                                        </p>
                                        <p className="mt-2 text-xs text-slate-300 leading-relaxed">
                                            {g.body}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCEDURES */}
            <section data-testid="inducext-procedures-section" className="border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
                    <div className="max-w-3xl">
                        <p className="eyebrow">Procedure Opportunities</p>
                        <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                            Where it fits.
                        </h2>
                        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                            A bone graft option for appropriate bone void filling during
                            foot and ankle reconstruction.
                        </p>
                    </div>
                    <ul className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {PROCEDURES.map((p) => (
                            <li
                                key={p}
                                data-testid={`inducext-procedure-${p.toLowerCase().replace(/\s+/g, "-")}`}
                                className="bg-[#FAFAFA] border border-slate-200 rounded-xl p-4 text-sm md:text-base text-slate-900 font-medium leading-snug"
                            >
                                {p}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-6 text-xs text-slate-500 italic leading-relaxed max-w-3xl">
                        Induce XT fills bone voids and gaps that are not intrinsic to
                        skeletal stability. It does not replace structural graft or
                        fixation. Follow the product instructions for use.
                    </p>
                </div>
            </section>

            {/* PRODUCT COMPARISON */}
            <ProductComparison />

            {/* REP OPPORTUNITY */}
            <section data-testid="inducext-rep-opp-section" className="border-b border-slate-200 bg-[#FAFAFA]">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
                    <div className="max-w-3xl">
                        <p className="eyebrow">For Reps</p>
                        <h2 className="font-display mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                            Support more of the case.
                        </h2>
                        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                            You already support the fixation and have the surgeon&rsquo;s
                            trust. Adding Induce XT creates an opportunity to bring a
                            complementary graft option into cases you already cover.
                        </p>
                    </div>
                    <article className="mt-6 border border-slate-200 bg-white rounded-2xl p-5 md:p-6 max-w-3xl">
                        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                            Case Example
                        </p>
                        <p className="mt-2 text-sm md:text-base text-slate-700 leading-relaxed">
                            Covering a TTC fusion? When supplemental graft is needed,
                            bring Induce XT into the conversation. Product selection
                            rests with the surgeon and requires facility approval.
                        </p>
                    </article>
                </div>
            </section>

            {/* PRODUCT RESOURCES (always visible) */}
            <InduceXTResources />

            {/* CLOSING CTA */}
            <section
                data-testid="inducext-closing-cta"
                className="bg-slate-900 text-white"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
                    <h2
                        data-testid="inducext-closing-headline"
                        className="font-display text-3xl md:text-5xl font-semibold tracking-tighter leading-[1.1] text-white max-w-3xl"
                    >
                        Bring Induce XT to your next conversation.
                    </h2>
                    <p className="mt-4 text-sm md:text-base text-slate-300 leading-relaxed max-w-2xl">
                        Schedule a Confidential Call to explore product fit and next steps.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Link
                            to={JOIN_URL}
                            data-testid="inducext-closing-reps-btn"
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 text-sm md:text-base font-medium px-7 py-3.5 rounded-md hover:bg-slate-100 transition-colors"
                        >
                            Schedule a Confidential Call
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
